import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { ApiError } from '../utils/fetchers';
import { TiloGame, TimeLeft, SortMode } from '../types/tilo/tilo-game';
import {
  formatTime,
  processRawGame,
  tiloGet,
  tiloPost
} from '../services/tilo';

export type { TiloGame, TiloTeam, TiloPlayer, TimeLeft, SortMode } from '../types/tilo/tilo-game';

/**
 * Manages TILO facilitator game state and all facilitator operations.
 * Polls the game every second and maintains a live countdown timer.
 * @param gameId - The MongoDB ObjectId of the TILO game to manage.
 * @param playlistId - Optional portal event playlist ID. When provided, starting
 *   or finishing the game will also update the portal event playlist status
 *   (Running / Completed) via the environment API.
 * @returns Game state and facilitator control functions:
 *   - game: Current processed game data, or null while loading.
 *   - isLoading: True until the first successful poll completes.
 *   - timeleft: Live countdown broken into { day, hour, min, sec } strings.
 *   - sortedTeams: Teams sorted by the active sortBy mode.
 *   - sortBy / setSortBy: Active sort mode and setter.
 *   - scoresHidden: Whether scores are currently hidden from players.
 *   - startGame / finishGame / addTeams / addTime / toggleScores: Game control actions.
 *   - addTeamScore / giveToken / kickPlayer: Per-team and per-player actions.
 */
export function useTiloFacilitator(gameId: string, playlistId?: number) {
  const { showBoundary } = useErrorBoundary();

  const [game, setGame] = useState<TiloGame | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeleft, setTimeleft] = useState<TimeLeft>(formatTime(0));
  const [sortBy, setSortBy] = useState<SortMode>('name');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [scoresHidden, setScoresHidden] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const countdownRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const countdownStartedRef = useRef(false);
  const isFirstPollRef = useRef(true);

  const stopCountdown = useCallback(() => {
    clearInterval(countdownRef.current);
    countdownRef.current = undefined;
    countdownStartedRef.current = false;
  }, []);

  const startCountdown = useCallback((g: TiloGame) => {
    if (countdownStartedRef.current) return;
    countdownStartedRef.current = true;

    const startedAt = (g.startedAt as Date).getTime();
    const endAt = startedAt + g.playtime * 60 * 1000;
    let remaining = Math.max(0, (endAt - Date.now()) / 1000);

    setTimeleft(formatTime(remaining));

    countdownRef.current = setInterval(() => {
      remaining = Math.max(0, remaining - 1);
      setTimeleft(formatTime(remaining));
    }, 1000);
  }, []);

  const poll = useCallback(async () => {
    try {
      const raw = await tiloGet(`tilo/games/${gameId}`);

      if (raw === null) {
        if (isFirstPollRef.current) {
          showBoundary(new ApiError('TILO game not found', 404));
        }
        return;
      }

      isFirstPollRef.current = false;
      const processed = processRawGame(raw);
      setGame(processed);
      setScoresHidden(processed.scoresHidden);
      setIsLoading(false);

      if (!processed.started) {
        stopCountdown();
        setTimeleft(formatTime(processed.playtime * 60));
      } else if (!processed.finished) {
        startCountdown(processed);
      } else {
        stopCountdown();
        setTimeleft(formatTime(0));
      }
    } catch (e: any) {
      if (isFirstPollRef.current) showBoundary(e);
    }
  }, [gameId, showBoundary, startCountdown, stopCountdown]);

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, 1000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(countdownRef.current);
    };
  }, [poll]);

  useEffect(() => {
    tiloGet('tilo/config/languages')
      .then((data) => { if (Array.isArray(data)) setLanguages(data); })
      .catch(() => {});
  }, []);

  const sortedTeams = useMemo(() => {
    if (!game) return [];
    let teams = [...game.teams];
    if (sortBy === 'name')
      teams.sort(
        (a, b) => parseInt(a.name.slice(5), 10) - parseInt(b.name.slice(5), 10)
      );
    else if (sortBy === 'score')
      teams.sort((a, b) => b.score - a.score);
    else if (sortBy === 'rank')
      teams.sort((a, b) => (a.rank && b.rank ? a.rank - b.rank : 0));
    if (selectedTeamIds.length > 0)
      teams = teams.filter((t) => selectedTeamIds.includes(t._id));
    return teams;
  }, [game, sortBy, selectedTeamIds]);

  const startGame = () =>
    tiloPost(`tilo/games/${gameId}/start`, { playlistId: playlistId ?? null });

  const finishGame = async () => {
    clearInterval(pollRef.current);
    stopCountdown();
    await tiloPost(`tilo/games/${gameId}/finish`, { playlistId: playlistId ?? null });
  };

  const addTeams = (teamsNbr: number) =>
    tiloPost(`tilo/games/${gameId}/add-teams`, { teamsNbr });

  const addTime = async (time: number) => {
    await tiloPost(`tilo/games/${gameId}/add-time`, { time });
    stopCountdown();
  };

  const changeLanguage = (language: string) =>
    tiloPost(`tilo/games/${gameId}/language`, { language });

  const toggleScores = async (hide: boolean) => {
    await tiloPost(`tilo/games/${gameId}/scores`, { hide });
    setScoresHidden(hide);
  };

  const addTeamScore = (teamId: string, score: number) =>
    tiloPost(`tilo/teams/${teamId}/score`, { score });

  const giveToken = (teamId: string, playerId: string) =>
    tiloPost(`tilo/teams/${teamId}/token`, { player: playerId });

  const kickPlayer = (playerId: string) =>
    tiloPost(`tilo/players/${playerId}/kick`);

  const disconnectPlayer = (playerId: string) =>
    tiloPost(`tilo/players/${playerId}/disconnect`);

  return {
    game,
    isLoading,
    timeleft,
    sortedTeams,
    sortBy,
    setSortBy,
    selectedTeamIds,
    setSelectedTeamIds,
    languages,
    scoresHidden,
    startGame,
    finishGame,
    addTeams,
    addTime,
    changeLanguage,
    toggleScores,
    addTeamScore,
    giveToken,
    kickPlayer,
    disconnectPlayer
  };
}